import useHeader from '@hooks/useHeader';
import { Trans, useTranslation } from 'react-i18next';
import {
    useMutation,
    useQueryClient,
    useSuspenseQuery,
} from '@tanstack/react-query';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Collapse,
    Icon,
    Stack,
    Typography,
} from '@mui/material';
import {
    ContentCopyRounded,
    DeleteOutlineRounded,
    DriveFileRenameOutlineRounded,
    InterestsRounded,
} from '@mui/icons-material';
import { useMemo } from 'react';
import { TransitionGroup } from 'react-transition-group';
import QuickOptions, { QuickOption } from '@Components/General/QuickOptions';
import { emailDesignsQueryOptions } from '@utils/queryOptions';
import { useLoaderDeps, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import useDialog from '@hooks/useDialog';
import DialogTextField from '@Components/General/dialog-content/text-field';
import Toolbar from '@Components/General/Toolbar';
import SimplePagination from '@Components/Common/SimplePagination';
import SimpleSort from '@Components/Common/SimpleSort';
import useSimplePagination from '@hooks/useSimplePagination';
import useSimpleSort from '@hooks/useSimpleSort';
import useEmptySearchParams from '@hooks/useEmptySearchParams';
import NoDesigns from './no-designs';
import NoDesignsFound from './no-designs-found';
import { maxToneToLevel } from '@utils/colorUtils';
import api from '@api/apiServices';

const DesignItem = ({ item }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const dialog = useDialog();
    const queryKey = emailDesignsQueryOptions(useLoaderDeps({})).queryKey;

    const { control } = useForm({});

    const deleteDesign = useMutation({
        mutationKey: ['deleteEmailDesign'],
        onSuccess: () => {
            dialog.close();
            queryClient.invalidateQueries({ queryKey });
        },
    });

    const { mutate: handleDuplicate } = useMutation({
        mutationKey: ['duplicateEmailDesign'],
        onSuccess: res => navigate({ to: res.id }),
    });

    const { mutate: createEmailDraft } = useMutation({
        mutationKey: ['createEmailDraft'],
        onSuccess: res => {
            navigate({ to: `../drafts/${res.id}/edit` });
        },
    });

    const getEmailDesign = api.emails.designs.get;

    const updateDesign = useMutation({
        mutationKey: ['updateEmailDesign'],
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey });
            dialog.close();
        },
    });

    const quickOptions: QuickOption[] = [
        {
            label: t('Rename'),
            onClick: () => {
                dialog?.open({
                    title: t('Rename Design'),
                    confirmLabel: t('Rename'),
                    content: (
                        <Stack width={400}>
                            <DialogTextField
                                fullWidth
                                name="title"
                                control={control}
                                defaultValue={item.title}
                                label={t('New Title')}
                            />
                            <DialogTextField
                                fullWidth
                                multiline
                                rows={4}
                                name="description"
                                control={control}
                                defaultValue={item.description}
                                label={t('Description')}
                            />
                        </Stack>
                    ),
                    onConfirm: () => [item.id, control._formValues],
                    confirmMutation: updateDesign,
                });
            },
            Icon: DriveFileRenameOutlineRounded,
        },
        {
            label: t('Edit'),
            onClick: () => navigate({ to: item.id }),
            Icon: props => <Icon {...props}>edit_square</Icon>,
        },
        {
            label: t('Make a Copy'),
            onClick: () => handleDuplicate(item.id),
            Icon: ContentCopyRounded,
        },
        {
            label: t('Delete'),
            Icon: DeleteOutlineRounded,
            sx: { color: 'error.main' },
            onClick: () =>
                dialog.open({
                    title: t('Delete Design') + '?',
                    message: (
                        <Trans t={t} values={{ title: item.title }}>
                            prompts:content.deleteDesign
                        </Trans>
                    ),
                    onConfirm: () => item.id,
                    confirmMutation: deleteDesign,
                    confirmButtonProps: {
                        color: 'error',
                    },
                    confirmLabel: t('Delete'),
                }),
        },
    ];

    return (
        <Card
            sx={{ display: 'flex', border: 'none', cursor: 'pointer' }}
            variant="outlined"
            onClick={() => navigate({ to: item.id })}
        >
            <Avatar
                sx={{
                    width: 146,
                    height: 146,
                    bgcolor: maxToneToLevel(item.background_color, 94),
                    borderRadius: 'inherit',
                }}
                src={item.thumbnail}
            >
                <InterestsRounded sx={{ fontSize: 112 }} />
            </Avatar>
            <CardContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <Box>
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant="titleLarge">
                            {item.title}
                        </Typography>
                        <Box>
                            <Box onClick={e => e.stopPropagation()}>
                                <QuickOptions options={quickOptions} />
                            </Box>
                        </Box>
                    </Box>
                    <Typography
                        variant="bodyMedium"
                        color="onSurfaceVariant.main"
                    >
                        {item.description}
                    </Typography>
                </Box>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography
                        variant="bodySmall"
                        color="onSurfaceVariant.main"
                    >
                        {t('Edited')}: {item.updated.toContextualDateTime()}
                    </Typography>
                    <Button
                        variant="tonal"
                        size="large"
                        color="secondary"
                        onClick={e => {
                            e.stopPropagation();
                            getEmailDesign(item.id).then(data =>
                                createEmailDraft({
                                    subject: t('Untitled Email'),
                                    template: data.template,
                                    // parent_template: template.id,
                                }),
                            );
                        }}
                    >
                        {t('Create Email')}
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};

const DesignList = ({ data }) => {
    return (
        <TransitionGroup component={Stack} spacing={2}>
            {data.map((design, index) => (
                <Collapse key={design.id}>
                    <DesignItem key={index} item={design} />
                </Collapse>
            ))}
        </TransitionGroup>
    );
};

const EmailDesigns = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const searchParamsEmpty = useEmptySearchParams();

    const simplePageHook = useSimplePagination();

    const simpleSortHook = useSimpleSort({
        inverseFields: ['created', 'updated'],
        defaultSortField: 'title',
    });

    const {
        data: { results, count },
    } = useSuspenseQuery(emailDesignsQueryOptions(useLoaderDeps({})));

    const headerOptions = useMemo(() => {
        return {
            title: t('Email Designs'),
            fabAction: () => () => navigate({ to: 'new' }),
            searchProps: {
                searchPlacholder: t('search:email-designs'),
            },
        };
    }, []);

    useHeader(headerOptions);

    if (results.length === 0 && searchParamsEmpty) {
        return <NoDesigns />;
    }

    return (
        <>
            <Toolbar>
                <SimpleSort
                    sortOptions={[
                        { value: 'title', label: t('Title') },
                        { value: 'created', label: t('Last Created') },
                        { value: 'updated', label: t('Last Updated') },
                    ]}
                    {...simpleSortHook}
                />
                <SimplePagination count={count} {...simplePageHook} />
            </Toolbar>
            {results.length === 0 ? (
                <NoDesignsFound />
            ) : (
                <DesignList data={results} />
            )}
        </>
    );
};

export default EmailDesigns;
